import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/utils/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        "destructive-ghost":
          "text-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 rounded gap-1 px-2 has-[>svg]:px-1 text-xs",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        icon_xs: "size-6",
        // Upstream mailtech-ui's `icon-xs` (the vendored query-builder kit
        // uses it): a 24px box whose bare icons shrink to 12px. `icon_xs`
        // above is the app's older key and stays unchanged for its callers.
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
      },
      loading: {
        true: "text-transparent relative",
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const loaderVariants = cva("animate-spin", {
  variants: {
    variant: {
      default: "text-primary-foreground",
      destructive: "text-destructive-foreground",
      outline: "text-accent-foreground",
      secondary: "text-secondary-foreground",
      ghost: "text-accent-foreground",
      "destructive-ghost": "text-destructive",
      link: "text-primary",
    },
    size: {
      default: "h-6 w-6",
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      lg: "h-8 w-8",
      icon: "h-6 w-6",
      icon_xs: "size-4",
      "icon-xs": "size-3",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  };

function Button({
  children,
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  const body = React.useMemo(() => {
    if (loading) {
      return (
        <>
          {children}
          <span className="animate-in fade-in-25 absolute inset-0 flex items-center justify-center">
            <Loader2
              data-testid="loading-spinner"
              className={cn(
                loaderVariants({
                  variant,
                  size,
                }),
              )}
            />
          </span>
        </>
      );
    }
    return children;
  }, [loading, children, size, variant]);

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className, loading }))}
      disabled={props.disabled ?? loading}
      {...props}
    >
      {body}
    </Comp>
  );
}

export { Button, buttonVariants };
